"use client"

import type React from "react"

import { useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  LayoutDashboard,
  Activity,
  Shield,
  BarChart3,
  Link,
  Settings,
  Users,
  Key,
  FileText,
  Zap,
  Database,
} from "lucide-react"

// Proper interfaces untuk type safety
interface SystemStats {
  activeVisitors?: number
  botBlocked?: number
  requestsPerSecond?: number
  botPercentage?: number
  avgResponseTime?: number
  totalRequests?: number
  uniqueVisitors?: number
}

interface MenuItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string | number | null
  isNew?: boolean
  isAlert?: boolean
}

interface DashboardSidebarProps {
  activeMenu: string
  setActiveMenu: (menu: string) => void
  systemStats?: SystemStats
  className?: string
}

export function DashboardSidebar({
  activeMenu,
  setActiveMenu,
  systemStats = {},
  className = "",
}: DashboardSidebarProps) {
  // Safe number formatting function
  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toLocaleString()
  }, [])

  const formatPercentage = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toFixed(1)
  }, [])

  // Safe menu item generation dengan proper error handling
  const generateMenuItems = useCallback((): MenuItem[] => {
    try {
      const safeStats = {
        activeVisitors: systemStats?.activeVisitors || 0,
        botBlocked: systemStats?.botBlocked || 0,
        totalRequests: systemStats?.totalRequests || 0,
      }

      return [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: LayoutDashboard,
          badge: null,
        },
        {
          id: "live-monitor",
          label: "Live Monitor",
          icon: Activity,
          badge: safeStats.activeVisitors > 0 ? safeStats.activeVisitors : null,
        },
        {
          id: "bot-detection",
          label: "Bot Detection",
          icon: Shield,
          badge: safeStats.botBlocked > 0 ? "!" : null,
          isAlert: safeStats.botBlocked > 0,
        },
        {
          id: "traffic-analytics",
          label: "Analytics",
          icon: BarChart3,
          badge: null,
        },
        {
          id: "campaign-control",
          label: "Campaigns",
          icon: Link,
          badge: null,
        },
        {
          id: "security-config",
          label: "Security",
          icon: Settings,
          badge: null,
        },
        {
          id: "ip-management",
          label: "IP Management",
          icon: Users,
          badge: null,
        },
        {
          id: "bypass-tokens",
          label: "Bypass Tokens",
          icon: Key,
          badge: null,
        },
        {
          id: "log-viewer",
          label: "Logs",
          icon: FileText,
          badge: null,
        },
        {
          id: "traffic-injection",
          label: "Traffic Injection",
          icon: Zap,
          badge: "NEW",
          isNew: true,
        },
        {
          id: "data-management",
          label: "Data Management",
          icon: Database,
          badge: null,
        },
        {
          id: "system-config",
          label: "System Config",
          icon: Settings,
          badge: null,
        },
      ]
    } catch (error) {
      console.error("Error generating menu items:", error)
      // Return basic menu items as fallback
      return [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: LayoutDashboard,
          badge: null,
        },
      ]
    }
  }, [systemStats])

  const menuItems = generateMenuItems()

  // Safe menu click handler
  const handleMenuClick = useCallback(
    (menuId: string) => {
      try {
        if (typeof setActiveMenu === "function") {
          setActiveMenu(menuId)
        }
      } catch (error) {
        console.error("Error setting active menu:", error)
      }
    },
    [setActiveMenu],
  )

  // Safe stats dengan default values dan proper validation
  const safeStats = {
    requestsPerSecond: formatNumber(systemStats?.requestsPerSecond),
    botPercentage: formatPercentage(systemStats?.botPercentage),
    avgResponseTime: formatNumber(systemStats?.avgResponseTime),
  }

  // Safe badge rendering function
  const renderBadge = useCallback(
    (item: MenuItem) => {
      if (!item.badge) return null

      try {
        const badgeVariant = item.isAlert
          ? "destructive"
          : item.isNew
            ? "default"
            : typeof item.badge === "number"
              ? "secondary"
              : "secondary"

        return (
          <Badge variant={badgeVariant} className="ml-2 text-xs">
            {typeof item.badge === "number" ? formatNumber(item.badge) : item.badge}
          </Badge>
        )
      } catch (error) {
        console.error("Error rendering badge:", error)
        return null
      }
    },
    [formatNumber],
  )

  return (
    <div className={`bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700 ${className}`}>
      <div className="space-y-2">
        <div className="mb-4">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Navigation</h2>
        </div>

        {menuItems.length > 0 ? (
          menuItems.map((item) => {
            try {
              const Icon = item.icon
              const isActive = activeMenu === item.id

              return (
                <Button
                  key={item.id}
                  variant={isActive ? "secondary" : "ghost"}
                  className={`w-full justify-start text-left h-10 transition-all duration-200 ${
                    isActive
                      ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                      : "text-slate-300 hover:text-white hover:bg-slate-700/50"
                  }`}
                  onClick={() => handleMenuClick(item.id)}
                >
                  <Icon className="w-4 h-4 mr-3 flex-shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {renderBadge(item)}
                </Button>
              )
            } catch (error) {
              console.error(`Error rendering menu item ${item.id}:`, error)
              return null
            }
          })
        ) : (
          <div className="text-center py-4 text-slate-500">
            <div className="text-sm">Menu items unavailable</div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-6 pt-4 border-t border-slate-700">
        <h3 className="text-sm font-medium text-slate-400 mb-3">Quick Stats</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-300">
            <span>Requests/sec</span>
            <span className="text-green-400 font-mono">{safeStats.requestsPerSecond}</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>Bot Rate</span>
            <span className="text-red-400 font-mono">{safeStats.botPercentage}%</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>Response Time</span>
            <span className="text-blue-400 font-mono">{safeStats.avgResponseTime}ms</span>
          </div>
        </div>
      </div>

      {/* System Status Indicator */}
      <div className="mt-4 pt-4 border-t border-slate-700">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">System Status</span>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-green-400">Online</span>
          </div>
        </div>
      </div>
    </div>
  )
}
